/**
 * Cuántos renglones de una lista de la columna derecha caben sin que la
 * pantalla ruede (30/09/2026, Norman: «no quiero scroll ni en vacaciones
 * ni permisos, paginado o diferente»). Lo usan «Tus vacaciones» y «Tus
 * permisos» para decidir de cuántos en cuántos paginar.
 *
 * Primero se estima (renglonesQueCaben): dónde empieza la lista dentro de
 * lo que rueda (.main-panel, el contenedor de Material Dashboard), cuánto
 * le queda hasta el pie de la ventana y cuántos renglones de un alto
 * típico caben ahí. Luego, ya pintada, se comprueba (seDesborda): un
 * renglón con su nota o su motivo mide más, y si los de la página no
 * caben, la pantalla quita uno y vuelve a probar.
 *
 * Sólo cuenta en el acomodo de dos columnas (.asi-inicio de 1040 px o
 * más, el mismo corte de asistente-comun.css): en una columna la lista va
 * debajo del asistente y la pantalla rueda de todos modos; ahí se usa
 * `enColumna`.
 */

/** Lo que queda debajo de la lista hasta el pie: el relleno de su
 *  tarjeta y el de la página. */
const PIE = 48;

export function renglonesQueCaben(
  lista: HTMLElement | null | undefined,
  altoRenglon: number,
  opciones: { minimo?: number; maximo?: number; enColumna?: number } = {}
): number | null {
  // Sin lista, o ya fuera de la página (se cambió de pestaña antes de que
  // llegaran las fuentes): nada que medir.
  if (!lista || !lista.isConnected) {
    return null;
  }
  const { minimo = 2, maximo = 6, enColumna = 4 } = opciones;
  if (!enDosColumnas(lista)) {
    return enColumna;
  }
  const m = medidas(lista);
  const libre = m.visible - m.arriba - PIE;
  return Math.max(minimo, Math.min(maximo, Math.floor(libre / altoRenglon)));
}

/** Si la lista, como quedó pintada, se pasa del pie de la ventana. */
export function seDesborda(lista: HTMLElement | null | undefined): boolean {
  if (!lista || !lista.isConnected || !enDosColumnas(lista)) {
    return false;
  }
  const m = medidas(lista);
  return m.abajo + PIE > m.visible;
}

function enDosColumnas(lista: HTMLElement): boolean {
  const rejilla = lista.closest(".asi-inicio") as HTMLElement | null;
  return !!rejilla && rejilla.clientWidth >= 1040;
}

/** Dónde empieza y dónde acaba la lista contando desde lo alto de lo que
 *  rueda (aunque ya se haya bajado un poco), y cuánto se ve. */
function medidas(lista: HTMLElement): { arriba: number; abajo: number; visible: number } {
  const r = lista.getBoundingClientRect();
  const panel = lista.closest(".main-panel") as HTMLElement | null;
  if (!panel) {
    return { arriba: r.top + window.scrollY, abajo: r.bottom + window.scrollY, visible: window.innerHeight };
  }
  const p = panel.getBoundingClientRect();
  return {
    arriba: r.top - p.top + panel.scrollTop,
    abajo: r.bottom - p.top + panel.scrollTop,
    visible: panel.clientHeight,
  };
}
